import { useState } from 'react'
import { apiURL } from '../../../utilities/URLs'
import { account } from '../../../classes/Account'
import { useNavigate } from 'react-router-dom'
import { refreshAccessToken } from '../../../utilities/extensions'
import {
  LISTED_STATE_OPTIONS_WITH_NON_US,
  NON_US_STATE_SELECT_VALUE,
  toStateSelectValue
} from '../../../utilities/usStateOptions'
export default function NewApplication({ agentId = '' }) {
  const navigate = useNavigate()

  const [entityName, setEntityName] = useState('')
  const [entityState, setEntityState] = useState('')
  const [withPEO, setWithPEO] = useState('')
  const [remainingERCCredits, setRemainingERCCredits] = useState('')
  const [filedAfterDate, setFiledAfterDate] = useState('')
  const [quarters, setQuarters] = useState([])
  const [submittingApplication, setSubmittingApplication] = useState(false)

  const isFormValid = entityName && String(entityState || '').trim() && remainingERCCredits && withPEO && filedAfterDate && (filedAfterDate !== 'Yes' || quarters.length > 0)

  const handleNewApplication = async () => {
    setSubmittingApplication(true)

    const newApplicationResponse = await fetch(`${apiURL}/api/applications/new/`, {
      method: 'POST',
      headers: {
        'Authorization': account.accessToken,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        entityName,
        entityState,
        remainingERCCredits,
        withPEO,
        filedAfterDate,
        quarters,
        agentId
      })
    })
    const newApplicationData = await newApplicationResponse.json()
    if (newApplicationData.response.error) {
      if (newApplicationData.response.error === 'Failed To Authenticate Token.') {
        const refreshedToken = await refreshAccessToken()
        refreshedToken && refreshedToken.length > 0 ? await handleNewApplication() : navigate('/', { state: { expired: true } })
      } else
        console.error(newApplicationData.response.error)
    } else {
      const { applicationId } = newApplicationData.response
      navigate(`/applications/${applicationId}/erc-agreement`)
    }

    setSubmittingApplication(false)
  }

  const fieldClassName =
    'mt-1 p-3 w-full border border-gray-300 dark:border-slate-600 rounded-md shadow-sm bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder:text-gray-400 dark:placeholder:text-slate-500 dark:[color-scheme:dark] focus:ring-2 focus:ring-ertc-primary/30 focus:border-ertc-primary outline-none transition-colors'

  const floatingLabelClassName =
    'absolute text-sm bg-white dark:bg-slate-900 text-gray-700 dark:text-slate-300 left-3 top-[-5px] max-sm:text-[0.7rem] max-sm:top-[-4px] px-0.5'

  return (
    <div className='w-full flex flex-1 flex-col gap-4 items-center text-slate-900 dark:text-gray-100'>
      <p className='font-bold text-lg p-4 w-full text-center'>Let's get some basic information.</p>
      <div className='flex flex-col items-center w-full space-y-8 px-4'>
        <div className='w-full space-y-4'>
          <h2 className='text-base font-semibold text-slate-900 dark:text-white'>Entity Information</h2>
          <div className='grid lg:grid-cols-2 gap-x-4 gap-y-4'>
            <div className='w-full relative'>
              <p className={floatingLabelClassName}>Entity Name</p>
              <input
                type='text'
                name='entityName'
                placeholder='My Entity'
                value={entityName}
                onChange={(e) => setEntityName(e.target.value)}
                className={fieldClassName}
              />
            </div>
            <div className='w-full relative'>
              <p className={floatingLabelClassName}>Entity State</p>
              <select
                name='entityState'
                value={toStateSelectValue(entityState)}
                onChange={(e) => {
                  const val = e.target.value
                  setEntityState(val === NON_US_STATE_SELECT_VALUE ? '' : val)
                }}
                className={fieldClassName}
              >
                <option value=''>--- SELECT A STATE ---</option>
                {LISTED_STATE_OPTIONS_WITH_NON_US.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </select>
              {toStateSelectValue(entityState) === NON_US_STATE_SELECT_VALUE && (
                <div className='mt-3'>
                  <label htmlFor='new-application-entity-state-other' className='block text-sm font-medium text-gray-700 dark:text-slate-300 mb-1'>
                    State / province / region
                  </label>
                  <input
                    id='new-application-entity-state-other'
                    type='text'
                    value={entityState}
                    onChange={(e) => setEntityState(e.target.value)}
                    placeholder='e.g. Guam, Ontario, England'
                    className={fieldClassName}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
        <div className='w-full space-y-4 border-t border-gray-200 dark:border-slate-700 pt-6'>
          <h2 className='text-base font-semibold text-slate-900 dark:text-white'>ERC Refund Amount</h2>
          <div className='w-full relative'>
            <p className={`${floatingLabelClassName} max-w-[calc(100%-1.5rem)]`}>Approximate Total ERC Refund the IRS Still Owes You</p>
            <input
              type='text'
              inputMode='numeric'
              pattern='[0-9]*'
              placeholder='120000'
              value={remainingERCCredits}
              onChange={(e) => {
                const value = e.target.value
                if (/^\d*$/.test(value))
                  setRemainingERCCredits(value)
              }}
              className={fieldClassName}
            />
          </div>
        </div>
        <div className='w-full space-y-4 border-t border-gray-200 dark:border-slate-700 pt-6'>
          <label className='text-base font-semibold text-slate-900 dark:text-white'>Were you associated with a Professional Employer Organization (PEO) or third-party payer (e.g., ADP TotalSource, Insperity, Paychex, TriNet) for payroll/tax filing during ERC periods?</label>
          <div className='flex gap-6'>
            {['Yes', 'No'].map(option => (
              <label key={option} className='flex items-center gap-3 text-gray-800 dark:text-slate-200 cursor-pointer'>
                <input
                  type='radio'
                  name='withPEO'
                  value={option}
                  checked={withPEO === option}
                  onChange={() => setWithPEO(option)}
                  className='w-5 h-5 accent-ertc-primary dark:accent-blue-500'
                />
                <span className='text-base'>{option}</span>
              </label>
            ))}
          </div>
          {withPEO === 'Yes' && (
            <div className='space-y-2 bg-blue-50 dark:bg-blue-950/40 p-4 rounded-md border border-blue-100 dark:border-blue-800/50'>
              <p className='font-bold text-lg text-slate-900 dark:text-white'>What is a PEO?</p>
              <p className='text-gray-700 dark:text-slate-300'>A Professional Employer Organization provides HR, payroll, and compliance services and may file tax documents, including ERC claims, under its own EIN. You likely used a PEO if:</p>
              <ul className='list-disc list-inside space-y-1 text-gray-700 dark:text-slate-300'>
                <li>Payroll reports list a PEO.</li>
                <li>Form 941s or ERC filings show a different EIN.</li>
                <li>W-2s display a third-party employer.</li>
                <li>HR/payroll is managed externally. (If unsure, check with your payroll provider or accountant.)</li>
              </ul>
            </div>
          )}
        </div>
        <div className='w-full space-y-4 border-t border-gray-200 dark:border-slate-700 pt-6'>
          <h2 className='text-base font-semibold text-slate-900 dark:text-white'>ERC Filing Details</h2>
          <div className='space-y-4'>
            <label className='text-base font-semibold text-slate-900 dark:text-white'>Did you file for your ERC refund after 1/31/24?</label>
            <div className='flex gap-6'>
              {['Yes', 'No'].map(option => (
                <label key={option} className='flex items-center gap-3 text-gray-800 dark:text-slate-200 cursor-pointer'>
                  <input
                    type='radio'
                    name='filedAfterDate'
                    value={option}
                    checked={filedAfterDate === option}
                    onChange={() => {
                      setFiledAfterDate(option)
                      if (option === 'No') setQuarters([])
                    }}
                    className='w-5 h-5 accent-ertc-primary dark:accent-blue-500'
                  />
                  <span className='text-base'>{option}</span>
                </label>
              ))}
            </div>
          </div>
          {filedAfterDate === 'Yes' && (
            <div className='space-y-4'>
              <label className='text-base font-semibold text-slate-900 dark:text-white'>Which 2021 quarters are your ERC claims not paid? (Select all that apply)</label>
              <div className='flex gap-6'>
                {[
                  { key: 'q12021', label: 'Q1 2021 (Jan-Mar)' },
                  { key: 'q22021', label: 'Q2 2021 (Apr-Jun)' },
                  { key: 'q32021', label: 'Q3 2021 (Jul-Sep)' },
                  { key: 'q42021', label: 'Q4 2021 (Oct-Dec)' },
                ].map(option => (
                  <label key={option.key} className='flex items-center gap-2 text-gray-700 dark:text-slate-300'>
                    <input
                      type='checkbox'
                      name={option.key}
                      value={option.key}
                      checked={quarters.includes(option.key)}
                      onChange={() => {
                        if (quarters.includes(option.key))
                          setQuarters(quarters.filter(q => q !== option.key))
                        else
                          setQuarters((prevQuarters) => [...prevQuarters, option.key])
                      }}
                      className='w-4 h-4 accent-ertc-primary dark:accent-blue-500'
                    />
                    {option.label}
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
        <button
          className={`px-8 py-3 text-white rounded-md transition-colors ${!isFormValid || submittingApplication ? 'bg-gray-400 dark:bg-slate-600 cursor-not-allowed' : 'bg-ertc-primary hover:bg-ertc-primary/90'}`}
          disabled={!isFormValid || submittingApplication}
          onClick={handleNewApplication}
        >
          Finish
        </button>
        {submittingApplication && (
          <div className='flex flex-row items-center justify-center gap-2 mt-5'>
            <div className='loader ease-linear rounded-full border-[6px] border-t-[6px] border-gray-200 dark:border-slate-700 border-t-ertc-primary dark:border-t-ertc-primary h-8 w-8 animate-spin'></div>
            <p className='text-md text-center text-slate-700 dark:text-slate-300'>Submitting Application...</p>
          </div>
        )}
      </div>
    </div>
  )
}